import React, { useState } from 'react';
import { Container } from 'react-bootstrap';
import HeaderAzul from '~/Components/HeaderAzul/HeaderAzul';
import { toast } from 'react-toastify';

import {
  processarComissaoImoveis,
  baixarRelatorioComissaoImoveis,
} from '~/Services/Service';
import { extrairMensagemErro, salvarPdf } from '~/Utils/Helpers';

import CardFiltros from './CardFiltros';
import ModalProcessamento from './ModalProcessamento';
import ModalHistorico from './ModalHistorico';

// W-0840 — Pro-Fit > Relatórios > Comissão de Imóveis
// Migração 1:1 do form Delphi TFComissao_Imoveis (UComissao_Imoveis.pas, ~2107 LoC).
// ADR-002 separa POST /processar de GET /relatorio/{id} (2ª via barata).
const RelatorioComissaoImoveis = () => {
  const [processando, setProcessando] = useState(false);
  const [modalProcessamentoAberto, setModalProcessamentoAberto] = useState(
    false
  );
  const [estadoProcessamento, setEstadoProcessamento] = useState({
    status: 'idle', // idle | processando | sucesso | erro
    resultado: null,
    erro: null,
    incluir: [],
  });
  const [modalHistoricoAberto, setModalHistoricoAberto] = useState(false);
  const [baixandoPdf, setBaixandoPdf] = useState(false);

  const construirListaIncluir = (filtros) => {
    const incluir = [];
    if (filtros.incluirImoveis) incluir.push('Imoveis');
    if (filtros.incluirPgto) incluir.push('Pgto');
    if (filtros.incluirSinal) incluir.push('Sinal');
    if (filtros.incluirExplicativo) incluir.push('Explicativo');
    if (filtros.emitirRecibo) incluir.push('Recibo');
    return incluir;
  };

  const construirPayloadRequest = (filtros) => ({
    codigo_grupo_empresa: Number(filtros.codigoGrupoEmpresa),
    codigo_conta:
      filtros.modo === 'UM_CORRETOR' && filtros.codigoConta
        ? Number(filtros.codigoConta)
        : null,
    data_inicial: filtros.dataInicial,
    data_final: filtros.dataFinal,
    incluir_imoveis: !!filtros.incluirImoveis,
    incluir_pgto: !!filtros.incluirPgto,
    incluir_sinal: !!filtros.incluirSinal,
    incluir_explicativo: !!filtros.incluirExplicativo,
    emitir_recibo: !!filtros.emitirRecibo,
  });

  const construirNomeArquivo = (resultado, filtros) => {
    const ge = filtros.codigoGrupoEmpresa || 'GE';
    const conta = filtros.codigoConta || 'todos';
    const dataRef =
      (resultado && resultado.gerado_em
        ? resultado.gerado_em.substring(0, 10)
        : '') || filtros.dataFinal;
    return `ComissaoImoveis_${ge}_${conta}_${dataRef}.pdf`;
  };

  const handleProcessar = (filtros) => {
    const incluir = construirListaIncluir(filtros);
    const payload = construirPayloadRequest(filtros);

    setProcessando(true);
    setModalProcessamentoAberto(true);
    setEstadoProcessamento({
      status: 'processando',
      resultado: null,
      erro: null,
      incluir,
      filtros,
    });

    processarComissaoImoveis(payload)
      .then((res) => {
        setEstadoProcessamento({
          status: 'sucesso',
          resultado: res,
          erro: null,
          incluir,
          filtros,
        });
      })
      .catch((err) => {
        const mensagem = extrairMensagemErro(err);
        setEstadoProcessamento({
          status: 'erro',
          resultado: null,
          erro: mensagem,
          incluir,
          filtros,
        });
      })
      .finally(() => {
        setProcessando(false);
      });
  };

  const handleBaixarPdf = ({ processamentoId, incluir, filtros, resultado }) => {
    if (!processamentoId) return;
    setBaixandoPdf(true);
    baixarRelatorioComissaoImoveis(processamentoId, incluir)
      .then((blob) => {
        salvarPdf(blob, construirNomeArquivo(resultado, filtros || {}));
      })
      .catch((err) => {
        toast.error(extrairMensagemErro(err));
      })
      .finally(() => {
        setBaixandoPdf(false);
      });
  };

  const handleFecharModalProcessamento = () => {
    // ADR-007: Botão "Fechar" durante processamento NÃO cancela a transação no servidor.
    // Apenas oculta a UI; copy explícito no modal.
    setModalProcessamentoAberto(false);
    if (estadoProcessamento.status !== 'processando') {
      setEstadoProcessamento({
        status: 'idle',
        resultado: null,
        erro: null,
        incluir: [],
      });
    }
  };

  const handleTentarNovamente = () => {
    if (!estadoProcessamento.filtros) return;
    handleProcessar(estadoProcessamento.filtros);
  };

  const handleBaixarPdfHistorico = (item) => {
    if (!item || !item.processamento_id) return;
    setBaixandoPdf(true);
    // Histórico baixa com todas as seções habilitadas (PRD §4.3 "2ª via" sem reconfigurar).
    const incluir = ['Imoveis', 'Pgto', 'Sinal', 'Explicativo', 'Recibo'];
    baixarRelatorioComissaoImoveis(item.processamento_id, incluir)
      .then((blob) => {
        const nome = `ComissaoImoveis_${item.codigo_grupo_empresa}_${item.codigo_conta}_${
          item.data ? String(item.data).substring(0, 10) : ''
        }.pdf`;
        salvarPdf(blob, nome);
      })
      .catch((err) => {
        toast.error(extrairMensagemErro(err));
      })
      .finally(() => {
        setBaixandoPdf(false);
      });
  };

  return (
    <Container fluid className="py-3">
      <HeaderAzul title="Pro-Fit — Comissão de Imóveis" />

      <CardFiltros
        onProcessar={handleProcessar}
        onAbrirHistorico={() => setModalHistoricoAberto(true)}
        processando={processando}
      />

      <ModalProcessamento
        aberto={modalProcessamentoAberto}
        estado={estadoProcessamento}
        baixandoPdf={baixandoPdf}
        onFechar={handleFecharModalProcessamento}
        onBaixarPdf={handleBaixarPdf}
        onTentarNovamente={handleTentarNovamente}
      />

      <ModalHistorico
        aberto={modalHistoricoAberto}
        onFechar={() => setModalHistoricoAberto(false)}
        onBaixarPdf={handleBaixarPdfHistorico}
        baixandoPdf={baixandoPdf}
      />
    </Container>
  );
};

export default RelatorioComissaoImoveis;
